import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Download, Minus, Plus } from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import RoundedCard from "@cle/components/RoundedCard";
import CleButton from "@cle/components/CleButton";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

type UploadedDocumentPreviewCardProps = {
  certificateTitle: string;
  fileBlob?: Blob;
  isError?: boolean;
  isLoading?: boolean;
};

const UploadedDocumentPreviewCard = ({
  certificateTitle,
  fileBlob,
  isError = false,
  isLoading = false,
}: UploadedDocumentPreviewCardProps) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1);
  const isZoomedIn = scale > 1;

  // Derived during render rather than stored in state, so the first paint
  // already has the URL. The effect only owns the revoke.
  const documentUrl = useMemo(
    () => (fileBlob ? URL.createObjectURL(fileBlob) : undefined),
    [fileBlob],
  );

  useEffect(() => {
    if (!documentUrl) {
      return undefined;
    }

    return () => URL.revokeObjectURL(documentUrl);
  }, [documentUrl]);

  const handleDownload = () => {
    if (!documentUrl) {
      return;
    }

    const link = document.createElement("a");
    link.href = documentUrl;
    link.download = certificateTitle;
    link.click();
  };

  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const handlePreviousPage = () => {
    setPageNumber((currentPage) => Math.max(currentPage - 1, 1));
  };

  const handleNextPage = () => {
    setPageNumber((currentPage) => Math.min(currentPage + 1, numPages));
  };

  const handleZoomOut = () => {
    setScale((currentScale) => Math.max(currentScale - 0.25, 0.5));
  };

  const handleZoomIn = () => {
    setScale((currentScale) => Math.min(currentScale + 0.25, 2));
  };

  return (
    <RoundedCard
      className="cle-course-certificate-document__preview-card h-100"
      header="Uploadet dokument"
      action={
        <CleButton
          id="cle-course-certificate-download"
          variant="outline"
          size="sm"
          onClick={handleDownload}
          disabled={!documentUrl}
        >
          <Download size={16} aria-hidden strokeWidth={1.75} />
          Download
        </CleButton>
      }
    >
      <div className="cle-course-certificate-document__viewer">
        <div className="cle-course-certificate-document__viewer-toolbar">
          <button
            type="button"
            className="btn btn-light btn-sm"
            onClick={handlePreviousPage}
            disabled={!documentUrl || pageNumber <= 1}
            aria-label="Forrige side"
          >
            <ChevronLeft size={14} aria-hidden />
          </button>
          <input
            className="form-control form-control-sm cle-course-certificate-document__page-input"
            value={documentUrl ? pageNumber : ""}
            readOnly
            aria-label="Side"
          />
          <span className="text-muted small">/ {numPages || "-"}</span>
          <button
            type="button"
            className="btn btn-light btn-sm"
            onClick={handleNextPage}
            disabled={!documentUrl || pageNumber >= numPages}
            aria-label="Næste side"
          >
            <ChevronRight size={14} aria-hidden />
          </button>
          <button
            type="button"
            className="btn btn-light btn-sm ms-auto"
            onClick={handleZoomOut}
            disabled={!documentUrl || scale <= 0.5}
            aria-label="Zoom ud"
          >
            <Minus size={14} aria-hidden />
          </button>
          <span className="small fw-semibold">{Math.round(scale * 100)}%</span>
          <button
            type="button"
            className="btn btn-light btn-sm"
            onClick={handleZoomIn}
            disabled={!documentUrl || scale >= 2}
            aria-label="Zoom ind"
          >
            <Plus size={14} aria-hidden />
          </button>
        </div>

        <div
          className={`cle-course-certificate-document__pdf-wrap ${
            isZoomedIn
              ? "cle-course-certificate-document__pdf-wrap--zoomed"
              : ""
          }`}
        >
          {isLoading ? (
            <div className="placeholder-glow w-100">
              <span className="placeholder cle-course-certificate-document__pdf-placeholder rounded" />
            </div>
          ) : null}
          {isError ? (
            <div className="alert alert-warning mb-0" role="alert">
              Dokumentet kunne ikke indlæses lige nu.
            </div>
          ) : null}
          {!isLoading && !isError && documentUrl ? (
            <Document
              file={documentUrl}
              loading={
                <div className="text-muted" role="status">
                  Indlæser PDF...
                </div>
              }
              error={
                <div className="alert alert-warning mb-0" role="alert">
                  PDF'en kunne ikke vises.
                </div>
              }
              onLoadSuccess={handleDocumentLoadSuccess}
            >
              <Page pageNumber={pageNumber} scale={scale} />
            </Document>
          ) : null}
        </div>
      </div>
    </RoundedCard>
  );
};

export default UploadedDocumentPreviewCard;
